<template>
  <div class="ams" ref="root" :style="rootStyle">
    <div
      class="ams-trigger"
      :class="{ 'is-active': visible }"
      :title="triggerTitle"
      @click="togglePanel"
      @mouseenter="hovering = true"
      @mouseleave="hovering = false"
    >
      <span
        class="ams-trigger__text"
        :class="{ 'is-placeholder': !committedOptions.length }"
      >{{ triggerText }}</span>
      <!-- 和 el-select 的 clearable 同一套：有选中项、鼠标又在触发器上时，箭头换成清空按钮。
           它和面板底部的「全部清除」不是一回事 —— 那个只改面板里的临时值、要点「应用」才生效，
           这个是直接把已提交的筛选清掉。
           两个图标共用这一个固定宽度的槽（照 el-input__suffix 的几何），
           所以切换时位置完全重合、不横向跳 -->
      <span class="ams-trigger__suffix">
        <i
          v-if="showClear"
          class="el-icon-circle-close ams-trigger__icon is-clear"
          @click.stop="clearCommitted"
        ></i>
        <i
          v-else
          class="el-icon-arrow-down ams-trigger__icon"
          :class="{ 'is-reverse': visible }"
        ></i>
      </span>
    </div>

    <div
      v-show="visible"
      ref="panel"
      class="ams-panel"
      :style="panelStyle"
    >
      <div class="ams-head">
        <div class="ams-head__left">
          <el-input
            v-if="searchable"
            v-model="keyword"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            :placeholder="t('search')"
          />
        </div>
        <div class="ams-head__right">
          <span class="ams-head__count">{{ selectedCountText }}</span>
          <span
            class="ams-head__clear"
            :class="{ 'is-disabled': !selectedCount }"
            @click="clearAll"
          >{{ t('clearAll') }}</span>
        </div>
      </div>

      <div class="ams-body">
        <!-- 选项列表 -->
        <div class="ams-col ams-col--options">
          <div v-if="visibleOptions.length" class="ams-row">
            <el-checkbox
              :value="allChecked"
              :indeterminate="indeterminate"
              @change="toggleAll"
            >{{ t('selectAll') }}</el-checkbox>
          </div>

          <!-- 分组模式：外层是应用，点开才显示它底下的广告位 -->
          <template v-if="isGrouped">
            <div v-for="g in filteredGroups" :key="g.value">
              <!-- 应用这一行：只有复选框本身是「整组勾选」，点名字和箭头都只是展开/收起 -->
              <div class="ams-row ams-row--group" @click="toggleExpand(g.value)">
                <el-checkbox
                  :value="isGroupChecked(g)"
                  :indeterminate="isGroupIndeterminate(g)"
                  @change="toggleGroup(g)"
                  @click.native.stop
                />
                <span class="ams-row__label" :title="g.label">{{ g.label }}</span>
                <i
                  class="el-icon-arrow-right ams-row__caret"
                  :class="{ 'is-expanded': isExpanded(g.value) }"
                ></i>
              </div>
              <div
                v-for="opt in g.children"
                v-show="isExpanded(g.value)"
                :key="opt.value"
                class="ams-row ams-row--child"
                :title="opt.label"
              >
                <el-checkbox
                  :value="isChecked(opt.value)"
                  @change="toggleOption(opt.value)"
                >{{ opt.label }}</el-checkbox>
              </div>
            </div>
          </template>

          <!-- 平铺模式 -->
          <template v-else>
            <div
              v-for="opt in filteredOptions"
              :key="opt.value"
              class="ams-row"
              :title="opt.label"
            >
              <el-checkbox
                :value="isChecked(opt.value)"
                @change="toggleOption(opt.value)"
              >{{ opt.label }}</el-checkbox>
            </div>
          </template>

          <p v-if="!visibleOptions.length" class="ams-empty">{{ t('noMatch') }}</p>
        </div>

        <!-- 已选项列表 -->
        <div class="ams-col ams-col--selected">
          <div
            v-for="opt in selectedOptions"
            :key="opt.value"
            class="ams-row ams-row--selected"
          >
            <span class="ams-row__label" :title="opt.label">{{ opt.label }}</span>
            <i class="el-icon-close ams-row__remove" @click="toggleOption(opt.value)"></i>
          </div>
          <p v-if="!selectedOptions.length" class="ams-empty">{{ t('noneSelected') }}</p>
        </div>
      </div>

      <div class="ams-footer">
        <el-button type="primary" size="mini" @click="apply">{{ t('apply') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AbroadMultiSelect',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    // 分组模式（广告位用）：[{ value, label, children: [{ value, label }] }]。
    // 传了它就走两级面板，外层是分组、点开才是子项；v-model 里存的仍然只有子项的 value，
    // 分组本身不是一个可提交的值（后端只收广告位），它只是「整组勾选」的入口
    groups: {
      type: Array,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    // 传 'auto' 时宽度跟着触发器上的文字走（英文文案比中文长，写死宽度会截断），
    // 夹在 minWidth ~ maxWidth 之间
    width: {
      type: [String, Number],
      default: 160
    },
    minWidth: {
      type: [String, Number],
      default: 160
    },
    maxWidth: {
      type: [String, Number],
      default: 320
    },
    panelWidth: {
      type: [String, Number],
      default: 460
    },
    searchable: {
      type: Boolean,
      default: true
    },
    align: {
      type: String,
      default: 'auto'
    }
  },
  data() {
    return {
      visible: false,
      // 鼠标是不是停在触发器上，只给清空按钮的显隐用
      hovering: false,
      keyword: '',
      // 面板挂在 body 上，位置每次打开时按触发器算出来（见 updatePosition）
      panelStyle: {},
      tempValue: [],
      expandedKeys: []
    }
  },
  computed: {
    isGrouped() {
      return Array.isArray(this.groups)
    },
    // 全选、已选区、触发器文案都只认「可提交的值」，分组模式下就是所有子项拍平
    allOptions() {
      if (!this.isGrouped) return this.options
      return this.groups.reduce((acc, g) => acc.concat(g.children || []), [])
    },
    cssWidth() {
      return typeof this.width === 'number' ? this.width + 'px' : this.width
    },
    // 根节点本来就是 inline-block，宽度给 auto 就会按内容收缩，再用 min/max 夹住
    rootStyle() {
      if (this.width !== 'auto') return { width: this.cssWidth }
      const px = v => (typeof v === 'number' ? v + 'px' : v)
      return { width: 'auto', minWidth: px(this.minWidth), maxWidth: px(this.maxWidth) }
    },
    cssPanelWidth() {
      return typeof this.panelWidth === 'number' ? this.panelWidth + 'px' : this.panelWidth
    },
    filteredOptions() {
      return this.matchOptions(this.options)
    },
    // 分组模式的搜索：命中分组名就整组保留，否则只留命中的子项，一个都没命中的分组不显示
    filteredGroups() {
      const kw = this.keyword.trim().toLowerCase()
      if (!kw) return this.groups
      const hit = []
      this.groups.forEach(g => {
        if (String(g.label).toLowerCase().indexOf(kw) !== -1) {
          hit.push(g)
        } else {
          const children = this.matchOptions(g.children)
          if (children.length) hit.push({ value: g.value, label: g.label, children: children })
        }
      })
      return hit
    },
    // 当前搜索结果里所有可勾选的子项，「全选」和「无匹配项」都以它为准
    visibleOptions() {
      if (!this.isGrouped) return this.filteredOptions
      return this.filteredGroups.reduce((acc, g) => acc.concat(g.children || []), [])
    },
    selectedOptions() {
      return this.allOptions.filter(o => this.tempValue.indexOf(o.value) !== -1)
    },
    selectedCount() {
      return this.tempValue.length
    },
    selectedCountText() {
      return String(this.t('selectedCountPattern')).replace('%n', this.selectedCount)
    },
    // 全选/半选都只针对当前搜索结果
    checkedInFiltered() {
      return this.visibleOptions.filter(o => this.isChecked(o.value)).length
    },
    allChecked() {
      return this.visibleOptions.length > 0 && this.checkedInFiltered === this.visibleOptions.length
    },
    indeterminate() {
      return this.checkedInFiltered > 0 && !this.allChecked
    },
    // 触发器上显示的是已提交的值（props），不是面板里的临时值
    committedOptions() {
      const v = Array.isArray(this.value) ? this.value : []
      return this.allOptions.filter(o => v.indexOf(o.value) !== -1)
    },
    // 每一项都选中 = 没有筛选，所以和「一个都不选」一样显示「全部」
    isAllCommitted() {
      return this.allOptions.length > 0 && this.committedOptions.length === this.allOptions.length
    },
    triggerText() {
      const list = this.committedOptions
      if (!list.length || this.isAllCommitted) return this.placeholder || this.t('all')
      if (list.length === 1) return list[0].label
      // 两项以上不再逐个列出（触发器只有 140~160px），改成计数，完整列表看 title 悬浮
      return String(this.t('triggerCountPattern')).replace('%n', list.length)
    },
    triggerTitle() {
      return this.committedOptions.map(o => o.label).join('、')
    },
    // 全选着的时候清空看着变化不大，但选中项确实是从「每一项」变成了「空」，
    // 请求体里的取值跟着变，所以只要有值就给清空按钮
    showClear() {
      return this.hovering && this.committedOptions.length > 0
    }
  },
  watch: {
    value() {
      // 父组件重置筛选条件时，面板里的临时值要跟着回到同一状态
      if (!this.visible) this.syncFromProps()
    }
  },
  mounted() {
    this.syncFromProps()
    // 面板挪到 body 底下：el-card / el-form-item 这些祖先带 overflow: hidden，
    // 留在原地会被裁成一条（和 el-select 的 popper 挂 body 是同一个道理）。
    // scoped 样式的 data-v 属性在元素自己身上，搬走了照样生效
    if (this.$refs.panel) document.body.appendChild(this.$refs.panel)
    document.addEventListener('mousedown', this.onDocMouseDown)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocMouseDown)
    this.unbindReposition()
    // 面板不在组件的 DOM 树里了，Vue 销毁时不会带走它，得自己摘
    const el = this.$refs.panel
    if (el && el.parentNode) el.parentNode.removeChild(el)
  },
  methods: {
    t(key) {
      return this.$t('multiSelect.' + key)
    },
    togglePanel() {
      if (this.visible) {
        this.closePanel()
      } else {
        this.syncFromProps()
        // 先挂上去但保持不可见 —— 面板要显示出来才量得到高宽，
        // 直接显示的话会在视口左上角闪一帧再跳到位
        this.panelStyle = { width: this.cssPanelWidth, visibility: 'hidden' }
        this.visible = true
        this.$nextTick(this.updatePosition)
        this.bindReposition()
      }
    },
    // 面板是 fixed 的，位置按触发器的视口坐标算：
    // 右边放不下就右对齐，下边放不下就翻到触发器上面，最后再夹回视口内
    updatePosition() {
      const root = this.$refs.root
      const panel = this.$refs.panel
      if (!this.visible || !root || !panel) return
      const gap = 4
      const edge = 8
      const r = root.getBoundingClientRect()
      const pw = panel.offsetWidth || parseInt(this.panelWidth, 10) || 0
      const ph = panel.offsetHeight || 0

      let left = r.left
      if (this.align === 'right' || (this.align === 'auto' && r.left + pw > window.innerWidth - edge)) {
        left = r.right - pw
      }
      left = Math.max(edge, Math.min(left, window.innerWidth - pw - edge))

      let top = r.bottom + gap
      if (top + ph > window.innerHeight - edge && r.top - gap - ph > edge) {
        top = r.top - gap - ph
      }

      this.panelStyle = {
        width: this.cssPanelWidth,
        left: left + 'px',
        top: top + 'px',
        visibility: 'visible'
      }
    },
    // 开着的时候页面滚动/窗口变化要跟着挪。scroll 用捕获，
    // 这样表格、侧栏这些内部滚动容器也能收到
    bindReposition() {
      window.addEventListener('resize', this.updatePosition)
      window.addEventListener('scroll', this.updatePosition, true)
    },
    unbindReposition() {
      window.removeEventListener('resize', this.updatePosition)
      window.removeEventListener('scroll', this.updatePosition, true)
    },
    closePanel() {
      this.visible = false
      this.unbindReposition()
      this.syncFromProps()
    },
    onDocMouseDown(e) {
      if (!this.visible) return
      if (this.$refs.root && this.$refs.root.contains(e.target)) return
      // 面板已经不在 root 里面了，得单独判一次，否则点面板本身就把它关了
      if (this.$refs.panel && this.$refs.panel.contains(e.target)) return
      this.closePanel()
    },
    syncFromProps() {
      this.tempValue = Array.isArray(this.value) ? this.value.slice() : []
      this.keyword = ''
      // 分组默认都收起，点行尾的箭头才展开
      this.expandedKeys = []
    },
    matchOptions(list) {
      const kw = this.keyword.trim().toLowerCase()
      if (!kw) return list || []
      return (list || []).filter(o => String(o.label).toLowerCase().indexOf(kw) !== -1)
    },
    isExpanded(key) {
      // 搜索时一律展开：命中的广告位要是还藏在收起的应用里，等于没搜到
      if (this.keyword.trim()) return true
      return this.expandedKeys.indexOf(key) !== -1
    },
    toggleExpand(key) {
      const i = this.expandedKeys.indexOf(key)
      if (i === -1) {
        this.expandedKeys.push(key)
      } else {
        this.expandedKeys.splice(i, 1)
      }
    },
    groupCheckedCount(g) {
      return (g.children || []).filter(o => this.isChecked(o.value)).length
    },
    isGroupChecked(g) {
      const total = (g.children || []).length
      return total > 0 && this.groupCheckedCount(g) === total
    },
    isGroupIndeterminate(g) {
      const checked = this.groupCheckedCount(g)
      return checked > 0 && checked < (g.children || []).length
    },
    // 分组的勾选框是「整组全选/全不选」，它自己不进 tempValue
    toggleGroup(g) {
      const children = g.children || []
      if (this.isGroupChecked(g)) {
        const drop = children.map(o => o.value)
        this.tempValue = this.tempValue.filter(v => drop.indexOf(v) === -1)
      } else {
        children.forEach(o => {
          if (!this.isChecked(o.value)) this.tempValue.push(o.value)
        })
      }
    },
    isChecked(val) {
      return this.tempValue.indexOf(val) !== -1
    },
    toggleOption(val) {
      const i = this.tempValue.indexOf(val)
      if (i === -1) {
        this.tempValue.push(val)
      } else {
        this.tempValue.splice(i, 1)
      }
    },
    toggleAll() {
      // 以当前是否已全选来决定这一次是全选还是全不选，只作用于搜索结果
      if (this.allChecked) {
        const drop = this.visibleOptions.map(o => o.value)
        this.tempValue = this.tempValue.filter(v => drop.indexOf(v) === -1)
      } else {
        this.visibleOptions.forEach(o => {
          if (!this.isChecked(o.value)) this.tempValue.push(o.value)
        })
      }
    },
    clearAll() {
      if (!this.selectedCount) return
      this.tempValue = []
    },
    // 触发器上的清空：直接提交空值。面板开着的时候临时值也一起清掉，
    // 不然面板里的勾还在、和触发器上显示的对不上
    clearCommitted() {
      this.tempValue = []
      this.$emit('input', [])
      this.$emit('change', [])
    },
    apply() {
      const next = this.tempValue.slice()
      this.visible = false
      this.unbindReposition()
      this.$emit('input', next)
      this.$emit('change', next)
    }
  }
}
</script>

<style lang="scss" scoped>
$main: #3361ff;
$text: #1f2329;
$text-sub: #4e5969;
$text-weak: #8f959e;
$line: #f0f1f3;
$row-h: 32px;

.ams {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  font-size: 14px; 
}

.ams-trigger {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 32px;
  /* 右边 30px 给后缀槽留位，和 el-input--suffix 的 padding-right 一致 */
  padding: 0 30px 0 10px;
  box-sizing: border-box;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    border-color: #c0c4cc;
  }
  &.is-active {
    border-color: $main;
  }
}
.ams-trigger__text {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &.is-placeholder {
    color: #c0c4cc;
  }
}
/* 后缀槽：照 el-select 的 .el-input__suffix 做 —— 固定宽度、绝对定位、图标在里面居中。
   做成固定宽度是为了让箭头和清空图标占同一块位置，鼠标进出时图标原地交换。
   之前两个都是 flex 末尾项、字号还一个 12px 一个 14px，切换时会横向跳一下 */
.ams-trigger__suffix {
  position: absolute;
  top: 0;
  right: 5px;
  height: 100%;
  width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 槽本身不吃点击，让箭头区域的点击照样冒泡到触发器去开面板；
     清空图标自己把 pointer-events 收回来（同 el-input__suffix-inner 的做法） */
  pointer-events: none;
}
.ams-trigger__icon {
  font-size: 14px;
  color: #c0c4cc;
  transition: transform 0.3s;

  &.is-reverse {
    transform: rotate(180deg);
  }
  &.is-clear {
    pointer-events: auto;
    cursor: pointer;

    &:hover {
      color: #909399;
    }
  }
}

/* 挂在 body 上的浮层：fixed + JS 定位（见 updatePosition），
   不受 el-card 那层 overflow: hidden 的裁剪 */
.ams-panel {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2100;
  background: #fff;
  // 挪到 body 之后不再继承 .ams 的字号，这里自己定一次
  font-size: 14px;
  border: 1px solid $line;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(31, 35, 41, 0.1), 0 2px 6px rgba(31, 35, 41, 0.04);
  overflow: hidden;
}

.ams-head {
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  border-bottom: 1px solid $line;
}
.ams-head__left {
  flex: 1;
  min-width: 0;
}
.ams-head__right {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 16px;
}
.ams-head__count {
  font-size: 13px;
  color: $text-sub;
}
.ams-head__clear {
  font-size: 13px;
  color: $main;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
  &.is-disabled {
    color: #c0c4cc;
    cursor: not-allowed;
    text-decoration: none;
  }
}

.ams-body {
  display: flex;
  height: 280px;
}
.ams-col {
  flex: 1;
  min-width: 0;
  padding: 8px 0;
  overflow-y: auto;
}
.ams-col--selected {
  border-left: 1px solid $line;
}

.ams-row {
  display: flex;
  align-items: center;
  height: $row-h;
  padding: 0 12px;
  box-sizing: border-box;

  &:hover {
    background: #f5f6f7;
  }
}
/* 让复选框铺满整行，点击行内任意位置都能勾选 */
.ams-row /deep/ .el-checkbox {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  margin-right: 0;
  // element 给 .el-checkbox 的默认字重是 500，比正文重一档，
  // 会让左栏比右栏「已选项」看着粗，这里拉回 400 让两栏一致
  font-weight: 400;
}
.ams-row /deep/ .el-checkbox__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  color: $text;
}

.ams-row--selected {
  justify-content: space-between;
}
/* 分组模式：应用行上只有复选框负责「整组勾选」，其余整行都是展开/收起 */
.ams-row--group {
  cursor: pointer;
}
.ams-row--group /deep/ .el-checkbox {
  flex: 0 0 auto;
  width: auto;
}
.ams-row--group .ams-row__label {
  margin-left: 8px;
}
/* 分组模式：子项（广告位）缩进一级，和上面的应用名区分开 */
.ams-row--child {
  padding-left: 32px;
}
.ams-row__caret {
  flex: 0 0 auto;
  margin-left: 8px;
  padding: 4px;
  font-size: 12px;
  color: $text-weak;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    color: $main;
  }
  &.is-expanded {
    transform: rotate(90deg);
  }
}
.ams-row__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  color: $text;
}
.ams-row__remove {
  flex: 0 0 auto;
  margin-left: 8px;
  font-size: 12px;
  color: $text-weak;
  cursor: pointer;

  &:hover {
    color: $main;
  }
}

.ams-empty {
  margin: 0;
  padding-top: 108px;
  text-align: center;
  font-size: 13px;
  color: $text-weak;
}

.ams-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  padding: 0 12px;
  border-top: 1px solid $line;
}
</style>
